import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ChatBubbleLeftRightIcon, 
  UserGroupIcon, 
  ClockIcon, 
  CheckCircleIcon,
  CogIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';
import Footer from '../components/Footer';
import FloatingThemeToggle from '../components/FloatingThemeToggle';

const HomePage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <FloatingThemeToggle />
      <div className="bg-gradient-to-br from-gray-900 light:from-gray-50 via-gray-800 light:via-gray-100 to-twitch-900 light:to-twitch-100 flex-1">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <ChatBubbleLeftRightIcon className="h-20 w-20 text-twitch-600" />
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white light:text-gray-900 mb-6">
              Waddup.me
            </h1>
            <p className="text-xl sm:text-2xl text-gray-400 light:text-gray-600 mb-8 max-w-3xl mx-auto">
              {t('home.subtitle')}
            </p>
            <p className="text-lg text-gray-300 light:text-gray-700 mb-12 max-w-4xl mx-auto">
              {t('home.description')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-twitch-600 hover:bg-twitch-700 transition-colors duration-200"
            >
              {t('home.cta.login')}
            </Link>
          </div>
        </div>

        {/* Features Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gray-800/50 light:bg-gray-200/50">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white light:text-gray-900 mb-4">{t('home.features.title')}</h2>
            <p className="text-lg text-gray-400 light:text-gray-600 max-w-2xl mx-auto">
              Built by streamers, for streamers. Every feature is designed to help you build meaningful connections with your audience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-700/50 light:bg-white/50 p-6 rounded-lg">
              <UserGroupIcon className="h-12 w-12 text-twitch-600 mb-4" />
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">{t('home.features.realtime.title')}</h3>
              <p className="text-gray-400 light:text-gray-600">
                {t('home.features.realtime.description')}
              </p>
            </div>

            <div className="bg-gray-700/50 light:bg-white/50 p-6 rounded-lg">
              <CheckCircleIcon className="h-12 w-12 text-twitch-600 mb-4" />
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">One-Click Greeting</h3>
              <p className="text-gray-400 light:text-gray-600">
                Mark viewers as greeted with a single click. Visual feedback ensures you never miss anyone or greet twice.
              </p>
            </div>

            <div className="bg-gray-700/50 light:bg-white/50 p-6 rounded-lg">
              <ClockIcon className="h-12 w-12 text-twitch-600 mb-4" />
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">Session Management</h3>
              <p className="text-gray-400 light:text-gray-600">
                Create separate greeting sessions for different streams. Perfect for organizing multi-day events or themed streams.
              </p>
            </div>

            <div className="bg-gray-700/50 light:bg-white/50 p-6 rounded-lg">
              <VideoCameraIcon className="h-12 w-12 text-twitch-600 mb-4" />
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">{t('home.features.obs.title')}</h3>
              <p className="text-gray-400 light:text-gray-600">
                {t('home.features.obs.description')}
              </p>
            </div>

            <div className="bg-gray-700/50 light:bg-white/50 p-6 rounded-lg">
              <CogIcon className="h-12 w-12 text-twitch-600 mb-4" />
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">Smart Filtering</h3>
              <p className="text-gray-400 light:text-gray-600">
                Ignore bots and specific users. Filter by keywords. Customize the experience to match your streaming style.
              </p>
            </div>

            <div className="bg-gray-700/50 light:bg-white/50 p-6 rounded-lg">
              <ChatBubbleLeftRightIcon className="h-12 w-12 text-twitch-600 mb-4" />
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">Real-Time Updates</h3>
              <p className="text-gray-400 light:text-gray-600">
                Instant notifications when new chatters arrive. Stay connected to your community as your stream grows.
              </p>
            </div>
          </div>
        </div>

        {/* Screenshots Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white light:text-gray-900 mb-4">See Waddup in Action</h2>
            <p className="text-lg text-gray-400 light:text-gray-600 max-w-2xl mx-auto">
              Get a preview of how Waddup fits into your streaming workflow
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Dashboard Screenshot Placeholder */}
            <div className="bg-gray-700/30 light:bg-gray-200/30 border-2 border-dashed border-gray-600 light:border-gray-400 rounded-lg p-8 text-center">
              <div className="mb-4">
                <UserGroupIcon className="h-16 w-16 text-gray-500 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">Dashboard View</h3>
              <p className="text-gray-400 light:text-gray-600 mb-4">
                Clean, intuitive interface showing all your active sessions and quick access to chatter management
              </p>
              <div className="text-sm text-gray-500">
                [Screenshot Coming Soon]
              </div>
            </div>

            {/* Session View Screenshot Placeholder */}
            <div className="bg-gray-700/30 light:bg-gray-200/30 border-2 border-dashed border-gray-600 light:border-gray-400 rounded-lg p-8 text-center">
              <div className="mb-4">
                <CheckCircleIcon className="h-16 w-16 text-gray-500 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">Greeting Session</h3>
              <p className="text-gray-400 light:text-gray-600 mb-4">
                Real-time chatter list with one-click greeting functionality and visual status indicators
              </p>
              <div className="text-sm text-gray-500">
                [Screenshot Coming Soon]
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* OBS View Screenshot Placeholder */}
            <div className="bg-gray-700/30 light:bg-gray-200/30 border-2 border-dashed border-gray-600 light:border-gray-400 rounded-lg p-8 text-center">
              <div className="mb-4">
                <VideoCameraIcon className="h-16 w-16 text-gray-500 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">{t('home.features.obs.title')}</h3>
              <p className="text-gray-400 light:text-gray-600 mb-4">
                Compact overlay view perfect for browser docks, keeps you connected without disrupting your stream
              </p>
              <div className="text-sm text-gray-500">
                [Screenshot Coming Soon]
              </div>
            </div>

            {/* Settings Screenshot Placeholder */}
            <div className="bg-gray-700/30 light:bg-gray-200/30 border-2 border-dashed border-gray-600 light:border-gray-400 rounded-lg p-8 text-center">
              <div className="mb-4">
                <CogIcon className="h-16 w-16 text-gray-500 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-white light:text-gray-900 mb-2">Customization</h3>
              <p className="text-gray-400 light:text-gray-600 mb-4">
                Extensive settings to tailor Waddup to your specific streaming needs and community style
              </p>
              <div className="text-sm text-gray-500">
                [Screenshot Coming Soon]
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-twitch-600/20 light:bg-twitch-600/10 border border-twitch-600/50 light:border-twitch-600/30 rounded-lg p-8 text-center">
            <h2 className="text-3xl font-bold text-white light:text-gray-900 mb-4">Ready to Level Up Your Community Building?</h2>
            <p className="text-lg text-gray-300 light:text-gray-700 mb-6 max-w-2xl mx-auto">
              Join streamers who have already improved their viewer engagement with Waddup. 
              Start building stronger connections with your community today.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-twitch-600 hover:bg-twitch-700 transition-colors duration-200"
            >
              Start Greeting Better Today
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default HomePage; 